import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Building, Check, ChevronsUpDown, Settings } from 'lucide-react';
import { useUserProfile } from '@/hooks/useSupabaseAuth';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { supabase } from '@/lib/supabase';
import { buildAdminUrl, buildWorkspaceUrl, resolveRoute } from '@/lib/workspace';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { AccountSettings } from '@/types/icrop';

interface PanelOption {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
}

/**
 * Header dropdown that lets a super_admin jump between the admin console and
 * any workspace. Each option is a full-page navigation (`<a href>`), not a
 * react-router `Link` — the Supabase session cookie is scoped to `.icrop.net`
 * so the cross-subdomain reload picks the session back up without re-login.
 *
 * Visibility: super_admin only. Non-super_admins get `null` here; the
 * single-workspace label they see is the account name baked into the Layout
 * header next to the iCrop logo.
 */
export function WorkspaceSwitcher() {
  const { data: profile } = useUserProfile();
  const { account } = useWorkspace();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const isSuperAdmin = profile?.role === 'super_admin';

  const { data: options = [] } = useQuery({
    queryKey: ['panel-options'],
    queryFn: async (): Promise<PanelOption[]> => {
      const { data, error } = await supabase
        .from('accounts')
        .select('id, name, slug, settings')
        .order('name');

      if (error) throw error;
      return (data ?? []).map((row) => ({
        id: row.id as string,
        name: row.name as string,
        slug: row.slug as string,
        logo_url:
          ((row.settings as AccountSettings | null)?.branding?.logo_url as string | undefined) ??
          null,
      }));
    },
    enabled: isSuperAdmin,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  if (!isSuperAdmin) {
    return null;
  }

  const route = resolveRoute();
  const activeSlug =
    route.kind === 'workspace' ? route.slug : account?.slug ?? null;
  const isAdminContext = route.kind === 'admin';

  const currentLabel = isAdminContext
    ? 'Admin console'
    : activeSlug
    ? options.find((o) => o.slug === activeSlug)?.name ?? activeSlug
    : 'Workspace';

  return (
    <div className="relative" ref={wrapperRef}>
      <Button
        variant="outline"
        size="sm"
        className="flex items-center gap-2"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Building className="h-4 w-4" />
        <span className="max-w-[160px] truncate">{currentLabel}</span>
        <ChevronsUpDown className="h-3.5 w-3.5 opacity-60" />
      </Button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-72 rounded-md border bg-popover text-popover-foreground shadow-lg z-50"
          role="menu"
        >
          <div className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground border-b">
            Switch panel
          </div>
          <a
            href={buildAdminUrl('/admin')}
            role="menuitem"
            className={cn(
              'flex items-center gap-3 px-3 py-2 text-sm hover:bg-muted transition-colors',
              isAdminContext && 'bg-muted/60',
            )}
          >
            <Settings className="h-4 w-4 text-muted-foreground" />
            <span className="flex-1">Admin console</span>
            {isAdminContext && <Check className="h-4 w-4 text-primary" />}
          </a>

          {options.length > 0 && (
            <div className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground border-t">
              Workspaces
            </div>
          )}
          <ul className="max-h-72 overflow-y-auto py-1">
            {options.map((opt) => {
              const isCurrent = !isAdminContext && activeSlug === opt.slug;
              return (
                <li key={opt.id}>
                  <a
                    href={buildWorkspaceUrl(opt.slug, '/dashboard')}
                    role="menuitem"
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 text-sm hover:bg-muted transition-colors',
                      isCurrent && 'bg-muted/60',
                    )}
                  >
                    {opt.logo_url ? (
                      <img
                        src={opt.logo_url}
                        alt=""
                        className="h-5 w-5 rounded-sm object-cover"
                      />
                    ) : (
                      <Building className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span className="flex-1 truncate">{opt.name}</span>
                    {isCurrent && <Check className="h-4 w-4 text-primary" />}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
